import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import apiService from '../services/apiService'

function EditCourse({ onClose, id }) {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm()
  const [isLoading, setIsLoading] = useState(true)
  const [selectedThumbnail, setSelectedThumbnail] = useState(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [isUploading, setIsUploading] = useState(false)
  const [currentThumbnailUrl, setCurrentThumbnailUrl] = useState('')
  const api = apiService()

  useEffect(() => {
    if (id) {
      getCourse(id)
    }
    // eslint-disable-next-line
  }, [id])

  const getCourse = async (id) => {
    try {
      const response = await api.get(`courses/${id}`)
      if (response.status === 200) {
        const course = response.data.course || response.data
        reset({
          title: course.title || '',
          description: course.description || '',
          thumbnail: course.thumbnail || '',
          status: course.status || 'draft',
        })
        setCurrentThumbnailUrl(course.thumbnail || '')
        setIsLoading(false)
      }
    } catch (error) {
      console.error('Error fetching course:', error)
      toast.error('Failed to fetch course details')
      setIsLoading(false)
    }
  }

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
      if (!validTypes.includes(file.type)) {
        toast.error('Invalid file type. Please select an image file (JPEG, PNG, GIF, WEBP).')
        e.target.value = ''
        return
      }
      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size too large. Maximum size is 10MB.')
        e.target.value = ''
        return
      }
      setSelectedThumbnail(file)
    }
  }

  const onSubmit = async (values) => {
    try {
      // First, update the course
      const response = await api.put(`courses/${id}`, {
        title: values.title,
        description: values.description || '',
        thumbnail: selectedThumbnail ? '' : (values.thumbnail || ''), // Clear if uploading new thumbnail
        status: values.status || 'draft',
      })

      if (response.status === 200) {
        // If a thumbnail file is selected, upload it
        if (selectedThumbnail) {
          setIsUploading(true)
          setUploadProgress(0)
          
          const formData = new FormData()
          formData.append('thumbnail', selectedThumbnail)

          try {
            await api.uploadFile(
              `courses/${id}/thumbnail`,
              formData,
              (progress) => setUploadProgress(progress)
            )
            toast.success('Course and thumbnail updated successfully')
          } catch (uploadError) {
            console.error('Error uploading thumbnail:', uploadError)
            toast.warning('Course updated but thumbnail upload failed. You can upload it later.')
          } finally {
            setIsUploading(false)
            setUploadProgress(0)
          }
        } else {
          toast.success('Course updated successfully')
        }
        
        if (onClose) onClose()
      }
    } catch (error) {
      console.error('Error updating course:', error)
      toast.error('Failed to update course')
    }
  }

  if (isLoading) {
    return (
      <div className="w-full max-w-2xl mx-auto p-0">
        <div className="text-center py-8">Loading course details...</div>
      </div>
    )
  }

  return (
    <form
      className="w-full max-w-2xl mx-auto p-0"
      onSubmit={handleSubmit(onSubmit)}
      autoComplete="off"
    >
      <div className="grid grid-cols-1 gap-6 mb-6">
        <div>
          <label className="block text-[#030303] font-medium mb-2">
            Course Title<span className="text-[#DE3C7D]">*</span>
          </label>
          <input
            type="text"
            className="w-full bg-[#FAFAFA] border-none rounded-xl px-5 py-3 text-[#030303] font-medium focus:ring-2 focus:ring-[#DE3C7D] outline-none"
            {...register('title', { required: 'Course title is required' })}
            placeholder="Enter course title"
          />
          {errors.title && (
            <p className="text-[#DE3C7D] text-xs mt-1">
              {errors.title.message}
            </p>
          )}
        </div>
        <div>
          <label className="block text-[#030303] font-medium mb-2">
            Description
          </label>
          <textarea
            rows={4}
            className="w-full bg-[#FAFAFA] border-none rounded-xl px-5 py-3 text-[#030303] font-medium focus:ring-2 focus:ring-[#DE3C7D] outline-none resize-none"
            {...register('description')}
            placeholder="Enter course description"
          />
        </div>
        <div>
          <label className="block text-[#030303] font-medium mb-2">
            Thumbnail
          </label>
          {currentThumbnailUrl && !selectedThumbnail && (
            <div className="mb-3 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-[#535862] mb-1">Current Thumbnail:</p>
              <img
                src={currentThumbnailUrl.startsWith('/uploads/') ? `${process.env.REACT_APP_BASE_URL?.replace(/\/$/, '')}${currentThumbnailUrl}` : currentThumbnailUrl}
                alt="Current thumbnail"
                className="w-full h-48 object-cover rounded-lg border border-[#F0F0F0] mb-2"
                onError={(e) => {
                  e.target.style.display = 'none'
                }}
              />
              <p className="text-xs text-[#535862] break-all">{currentThumbnailUrl}</p>
            </div>
          )}
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-[#535862] mb-2">
                Upload New Thumbnail Image (JPEG, PNG, GIF, WEBP - Max 10MB)
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleThumbnailChange}
                className="w-full bg-[#FAFAFA] border-none rounded-xl px-5 py-3 text-[#030303] font-medium focus:ring-2 focus:ring-[#DE3C7D] outline-none file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#DE3C7D] file:text-white hover:file:bg-[#c8356f] cursor-pointer"
              />
              {selectedThumbnail && (
                <div className="mt-3">
                  <img
                    src={URL.createObjectURL(selectedThumbnail)}
                    alt="Preview"
                    className="w-full h-48 object-cover rounded-lg border border-[#F0F0F0]"
                  />
                  <p className="text-sm text-[#535862] mt-2">
                    Selected: {selectedThumbnail.name} ({(selectedThumbnail.size / (1024 * 1024)).toFixed(2)} MB)
                  </p>
                </div>
              )}
              {isUploading && (
                <div className="mt-2">
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                      className="bg-[#DE3C7D] h-2.5 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-[#535862] mt-1">Uploading: {uploadProgress}%</p>
                </div>
              )}
            </div>
            <div className="text-center text-sm text-[#535862]">OR</div>
            <div>
              <label className="block text-sm text-[#535862] mb-2">
                Enter Thumbnail URL (External Link)
              </label>
              <input
                type="url"
                className="w-full bg-[#FAFAFA] border-none rounded-xl px-5 py-3 text-[#030303] font-medium focus:ring-2 focus:ring-[#DE3C7D] outline-none"
                {...register('thumbnail')}
                placeholder="https://example.com/image.jpg"
                disabled={!!selectedThumbnail}
              />
            </div>
          </div>
        </div>
        <div>
          <label className="block text-[#030303] font-medium mb-2">
            Status<span className="text-[#DE3C7D]">*</span>
          </label>
          <select
            className="w-full bg-[#FAFAFA] border-none rounded-xl px-5 py-3 text-[#030303] font-medium focus:ring-2 focus:ring-[#DE3C7D] outline-none"
            {...register('status', { required: 'Status is required' })}
          >
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          {errors.status && (
            <p className="text-[#DE3C7D] text-xs mt-1">
              {errors.status.message}
            </p>
          )}
        </div>
      </div>
      <div className="flex flex-col md:flex-row gap-4 mt-8">
        <button
          type="button"
          onClick={onClose}
          className="w-full md:w-1/2 px-4 py-3 rounded-xl border border-[#E0E0E0] text-[#030303] bg-white font-medium hover:bg-neutral-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="w-full md:w-1/2 px-4 py-3 rounded-xl bg-[#DE3C7D] text-white font-medium hover:bg-[#c8356f] transition-colors"
        >
          Update Course
        </button>
      </div>
    </form>
  )
}

export default EditCourse

